import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ContentBlock, { ExpandableBlocks } from 'components/ContentBlock'
import DxDataTable from 'components/DxDataTable'
import 'devextreme/dist/css/dx.light.css'

import { ReactComponent as Back } from 'media/icons/rc_arrow_back.svg'
import { ReactComponent as Home } from 'media/icons/rc_circle_outline.svg'
import { ReactComponent as Stack } from 'media/icons/rc_box_outline.svg'
import { ReactComponent as Power } from 'media/icons/rc_power.svg'
import { ReactComponent as Restart } from 'media/icons/rc_restart.svg'
import { ReactComponent as Sidebar } from 'media/icons/rc_sidebar.svg'
import { ReactComponent as Display } from 'media/icons/rc_display.svg'
import { ReactComponent as ExitKiosk } from 'media/icons/rc_exitkisok.svg'
import { ReactComponent as RequestPermissions } from 'media/icons/rc_requestpermissions.svg'
import { EndSession } from 'media/icons'

import RemoteControlButton from './RemoteControlButton'
import RemoteControlVideo from './RemoteControlVideo'
import FileManager from './FileManager'
import { StyledMenuBar, StreamContainer, StyledMenuSubgroup, StreamLoader, StreamRestarter, StatusText } from './styled'
import useStreamViewer from './hooks/useStreamViewer'
import ItemActions from 'components/SectionHandler/ItemActions'
import { MenuIcon } from 'media/icons'

const iconStyles = `
	width: 20px;
	height: 20px;
`

const fullWidthSubActionStyles = `
	width: 100%;
	box-sizing: border-box;
`

const RemoteAccess = ({ data, appData, queryResult, resetPage, appManagerVersion, isDeviceOnline, ...rest }) => {
	const videoRef = useRef()
	const [isSideDisplayOn, setSideDisplayState] = useState(false)
	const [statusText, setStatusText] = useState('')
	const [logsArray, setLogsArray] = useState([])
	const statusTextTimer = useRef(null)
	const isMounted = useRef(true)
	const [t] = useTranslation()

	useEffect(() => {
		isMounted.current = true
		return () => {
			isMounted.current = false
			if(statusTextTimer.current) {
				clearTimeout(statusTextTimer.current)
				statusTextTimer.current = null
			}
		}
	}, [])

	const handleDeviceStateChange = (state) => {
		if(state === 'connecting') {
			handleSetStatusText('[PEER CONNECTION] status: connecting')
			return
		}
		handleSetStatusText(`[DEVICE] status: ${JSON.stringify(state)}`)
	}

	const handleSetStatusText = text => {
		if(statusTextTimer.current) {
			clearTimeout(statusTextTimer.current)
		}
		statusTextTimer.current = setTimeout(() => {
			if(!isMounted.current) {
				return
			}
			setStatusText(null)
			statusTextTimer.current = null
		}, 5 * 1000)
		if(isMounted.current) {
			setStatusText(text)
		}
	}

	const res = useStreamViewer({
		videoRef,
		hardwareId: data?.hardwareId,
		clientId: appData.authData.attributes.sub,
		appManagerVersion,
		onMessage: (message) => {
			if(message.PROGRESS) {
				handleSetStatusText(`[DOWNLOAD] ${message.PROGRESS}%`)
			}
			if(message.type === 'LOG' && isMounted.current) {
				setLogsArray(prev => [...prev, message])
			}
		},
		onError: () => handleSetStatusText(`[ERROR] ${JSON.stringify(hasError)}`),
		onDeviceStateChange: handleDeviceStateChange
	})
	const {
		isLoading,
		hasClosed,
		hasError,
		sendCommandMessage,
		sendRequest,
		endSession,
		isDataChannelReady
	} = res

	const handleSideDisplayButton = async () => {
		const command = isSideDisplayOn ? 'PRESENTATION_OFF' : 'PRESENTATION_ON'
		const result = await sendRequest({ command })
		if(result[command] === 'SUCCESS') {
			setSideDisplayState(command === 'PRESENTATION_ON' ? true : false)
		}
	}

	const handleChangeResolution = (name) => {
		sendCommandMessage({ command: 'CHANGE_RESOLUTION', name })
	}

	const actionProps = {
		iconStyles,
		checkAvailability: () => !isLoading
	}

	return(
		<ExpandableBlocks>
			{!isDeviceOnline &&
				<ContentBlock alwaysExpanded showHeader={false}>{t('options_section.strings.device_may_be_offline')}</ContentBlock>
			}
			<StreamContainer>
				{isLoading &&
					<StreamLoader isLoading/>
				}
				{(hasClosed || hasError) &&
					<StreamRestarter >
						<div onClick={resetPage}>
							<Restart/>
							Restart stream
						</div>
					</StreamRestarter>
				}
				<RemoteControlVideo
					id='streamPlayerId'
					handleClick={coordinates => sendCommandMessage({ command: 'SINGLE_TAP', coordinates })}
					handleDoubleClick={coordinates => sendCommandMessage({ command: 'DOUBLE_TAP', coordinates })}
					handleLongPress={coordinates => sendCommandMessage({ command: 'LONG_TAP', coordinates })}
					handleSwipe={coordArr => sendCommandMessage({ command: 'SWIPE', coordinatesStart: coordArr[0], coordinatesEnd: coordArr[1] })}
					videoRef={videoRef}
					muted
					type='video/mp4'
					autoPlay
					loop
					playsInline
				/>
				{!!statusText && <StatusText>{statusText}</StatusText>}
				<StyledMenuBar>
					<ItemActions
						trigger={<MenuIcon />}
						direction='top'
						actions={[
							{ label: t('options_section.fields.open_sidebar'), Icon: Sidebar, action: () => sendCommandMessage({ command: 'SIDEBAR' }), longPress: () => sendCommandMessage({ command: 'TAKE_LOCAL_DB_SNAPSHOT' }), ...actionProps },
							{ label: t('options_section.fields.secondary_display'), Icon: Display, action: handleSideDisplayButton, ...actionProps },
							{
								label: t('options_section.fields.stream_resolution'),
								Icon: Display,
								subActions: [
									{ label: t('options_section.fields.resolution_low'), action: () => handleChangeResolution('LOW'), additionalStyles: fullWidthSubActionStyles, ...actionProps },
									{ label: t('options_section.fields.resolution_medium'), action: () => handleChangeResolution('MEDIUM'), additionalStyles: fullWidthSubActionStyles, ...actionProps },
									{ label: t('options_section.fields.resolution_high'), action: () => handleChangeResolution('HIGH'), additionalStyles: fullWidthSubActionStyles, ...actionProps }
								],
								...actionProps
							},
							{ label: t('options_section.fields.exit_kiosk'), Icon: ExitKiosk, action: () => sendCommandMessage({ command: 'EXIT_KIOSK' }), ...actionProps },
							{ label: t('options_section.fields.request_permissions'), Icon: RequestPermissions, action: () => sendCommandMessage({ command: 'REQUEST_SCREEN_CAPTURE_PERMISSIONS' }), ...actionProps },
							{ label: t('options_section.fields.end_session'), Icon: EndSession, action: endSession, iconStyles }
						]}
					/>
					{/* <StyledMenuSubgroup width='32px'> */}
					{/* <RemoteControlButton tooltip={t('options_section.fields.open_sidebar')} onClick={() => sendCommandMessage({ command: 'SIDEBAR' })} onLongClick={() => sendCommandMessage({ command: 'TAKE_LOCAL_DB_SNAPSHOT' })}><Sidebar/></RemoteControlButton> */}
					{/* <RemoteControlButton tooltip={t('options_section.fields.secondary_display')} onClick={handleSideDisplayButton}><Display/></RemoteControlButton> */}
					{/* <RemoteControlButton tooltip={t('options_section.fields.exit_kiosk')} onClick={() => sendCommandMessage({ command: 'EXIT_KIOSK' })}><ExitKiosk/></RemoteControlButton> */}
					{/* <RemoteControlButton tooltip={t('options_section.fields.request_permissions')} onClick={() => sendCommandMessage({ command: 'REQUEST_SCREEN_CAPTURE_PERMISSIONS' })}><RequestPermissions/></RemoteControlButton> */}
					{/* <RemoteControlButton tooltip={t('options_section.fields.end_session')} onClick={endSession}><EndSession/></RemoteControlButton> */}
					{/* <RemoteControlButton onClick={() => sendCommandMessage({ command: 'COPY' })}><Copy/></RemoteControlButton> */}
					{/* <RemoteControlButton onClick={() => sendCommandMessage({ command: 'PASTE' })}><Paste/></RemoteControlButton> */}
					{/* </StyledMenuSubgroup> */}
					{/* <StyledMenuSubgroup width='96px'> */}
					{/* <RemoteControlButton onClick={() => sendCommandMessage({ command: 'COPY' })}><Copy/></RemoteControlButton> */}
					{/* <RemoteControlButton onClick={() => sendCommandMessage({ command: 'PASTE' })}><Paste/></RemoteControlButton> */}
					{/* </StyledMenuSubgroup> */}
					<StyledMenuSubgroup width='256px'>
						<RemoteControlButton onClick={() => sendCommandMessage({ command: 'BACK' })}><Back/></RemoteControlButton>
						<RemoteControlButton onClick={() => sendCommandMessage({ command: 'HOME' })}><Home/></RemoteControlButton>
						<RemoteControlButton onClick={() => sendCommandMessage({ command: 'STACK' })}><Stack/></RemoteControlButton>
					</StyledMenuSubgroup>

					<RemoteControlButton onClick={() => sendCommandMessage({ command: 'POWER' })}><Power/></RemoteControlButton>
				</StyledMenuBar>
			</StreamContainer>
			<ContentBlock label={t('options_section.strings.file_system')}>
				{isDataChannelReady && <FileManager sendRequest={sendRequest}/>}
			</ContentBlock>
			<ContentBlock label={t('options_section.strings.log_file')}>
				<DxDataTable
					height={500}
					withSearch={false}
					columns={[{
						caption: t('common.strings.actions'),
						dataField: 'action'
					}, {
						caption: t('common.strings.target'),
						dataField: 'target'
					}, {
						caption: t('common.strings.timestamp'),
						dataField: 'time',
						dataType: 'datetime',
						sortIndex: 1,
						sortOrder: 'desc'
					}]}
					data={logsArray}
				/>
			</ContentBlock>
		</ExpandableBlocks>
	)
}

export default RemoteAccess
